import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import EnhancedQuestionDisplay from './EnhancedQuestionDisplay';
import UniversalHeader from './UniversalHeader';

/**
 * Enhanced Practice Session Component with Mathematical Playground Design
 * Features: Vibrant timer, animated progress, playful question navigation
 * Maintains all existing functionality while improving visual engagement
 */

const EnhancedPracticeSession = ({ sessionData, onComplete, onBack }) => {
  const { user } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [sessionScore, setSessionScore] = useState(0);
  const [startTime, setStartTime] = useState(new Date());
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [debugLogged, setDebugLogged] = useState(false);
  const [streak, setStreak] = useState(0);

  const questions = sessionData?.questions || [];
  const sessionInfo = sessionData?.session || {};
  const totalDuration = (sessionInfo.duration_minutes || 15) * 60;

  useEffect(() => {
    if (!debugLogged) {
      console.log('🔍 INIT: Full sessionData structure:', JSON.stringify(sessionData, null, 2));
      setDebugLogged(true);
    }

    const isResumed = sessionInfo.is_paused && sessionInfo.current_question_index !== undefined;

    if (isResumed) {
      console.log('🔄 Resuming paused session');
      setCurrentQuestionIndex(sessionInfo.current_question_index || 0);
      setAnswers(sessionInfo.answers_so_far || {});
      setTotalTimeSpent(sessionInfo.time_spent_seconds || 0);
      const adjustedDuration = totalDuration - (sessionInfo.time_spent_seconds || 0);
      setTimeRemaining(Math.max(adjustedDuration, 60));
    } else {
      setTimeRemaining(totalDuration);
      setTotalTimeSpent(0);
    }

    setStartTime(new Date());
    setIsInitialized(true);
    setIsTimerActive(true);
  }, [totalDuration, sessionInfo]);

  useEffect(() => {
    if (isInitialized && isTimerActive && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (isInitialized && timeRemaining === 0 && isTimerActive) {
      finishSession();
    }
  }, [timeRemaining, isTimerActive, isInitialized]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionIndex]: answerIndex
      };

      // Check if answer is correct and update streak
      const question = questions[questionIndex];
      let correctAnswerIndex;
      if (question.correct_index !== undefined) {
        correctAnswerIndex = question.correct_index;
      } else if (typeof question.correct_answer === 'string' && question.correct_answer.match(/^[A-D]$/)) {
        correctAnswerIndex = question.correct_answer.charCodeAt(0) - 'A'.charCodeAt(0);
      } else {
        correctAnswerIndex = question.options.indexOf(question.correct_answer);
      }

      if (answerIndex === correctAnswerIndex) {
        setStreak(s => s + 1);
      } else {
        setStreak(0);
      }

      return newAnswers;
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      finishSession();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const finishSession = () => {
    setIsTimerActive(false);
    calculateAndSaveResults();
  };

  const calculateAndSaveResults = async () => {
    let correctAnswers = 0;
    const totalAnswered = Object.keys(answers).length;

    questions.forEach((question, index) => {
      const userAnswer = answers[index];

      if (userAnswer !== undefined) {
        let correctAnswerIndex;
        if (question.correct_index !== undefined) {
          correctAnswerIndex = question.correct_index;
        } else if (typeof question.correct_answer === 'string' && question.correct_answer.match(/^[A-D]$/)) {
          correctAnswerIndex = question.correct_answer.charCodeAt(0) - 'A'.charCodeAt(0);
        } else {
          correctAnswerIndex = question.options.indexOf(question.correct_answer);
        }

        if (userAnswer === correctAnswerIndex) {
          correctAnswers++;
        }
      }
    });

    const score = totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0;
    setSessionScore(score);

    try {
      const endTime = new Date();
      const actualDuration = Math.round((endTime - startTime) / 1000 / 60);

      const resultData = {
        session_id: sessionInfo.id,
        user_id: user?.id,
        questions_answered: totalAnswered,
        correct_answers: correctAnswers,
        score: score,
        actual_duration_minutes: actualDuration,
        answers: answers,
        completed_at: endTime.toISOString(),
        subtopic_id: sessionInfo.subtopic_id,
        difficulty_level: sessionInfo.difficulty_level,
        learning_path_id: sessionInfo.learning_path_id
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const isLearningPathSession = sessionInfo.subtopic_id || sessionInfo.learning_path_id;
      const completeEndpoint = isLearningPathSession
        ? `${backendUrl}/api/learning-path/practice/complete-session`
        : `${backendUrl}/api/practice/complete-session`;

      const response = await fetch(completeEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(resultData)
      });

      if (!response.ok) {
        if (response.status === 404 && isLearningPathSession) {
          const fallbackResponse = await fetch(`${backendUrl}/api/practice/complete-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(resultData)
          });

          if (fallbackResponse.ok) {
            const fallbackResult = await fallbackResponse.json();
            console.log('✅ Practice session saved via fallback endpoint:', fallbackResult);
          }
        }
      } else {
        const result = await response.json();
        console.log('✅ Practice session saved successfully:', result);
      }
    } catch (err) {
      console.error('Error saving practice session:', err);
    }

    setShowResults(true);
  };

  const pauseSession = async () => {
    if (window.confirm('Are you sure you want to pause this session? You can resume it later.')) {
      setIsTimerActive(false);
      setIsPaused(true);

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const currentTimeSpent = totalTimeSpent + (totalDuration - timeRemaining);
        const isLearningPathSession = sessionInfo.subtopic_id || sessionInfo.learning_path_id;
        const pauseEndpoint = isLearningPathSession
          ? `${backendUrl}/api/learning-path/practice/pause-session`
          : `${backendUrl}/api/practice/pause-session`;

        await fetch(pauseEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            session_id: sessionInfo.id,
            user_id: user?.id,
            current_question_index: currentQuestionIndex,
            answers_so_far: answers,
            time_spent_seconds: currentTimeSpent
          })
        });

        onBack();
      } catch (err) {
        console.error('Error pausing session:', err);
        onBack();
      }
    }
  };

  const skipToEnd = () => {
    if (window.confirm('Are you sure you want to finish this practice session early?')) {
      finishSession();
    }
  };

  if (questions.length === 0) {
    return (
      <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="mc-card text-center" style={{ maxWidth: '500px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 className="mc-heading-1" style={{ color: 'var(--mc-error)', marginBottom: '1rem' }}>
            Session Error
          </h2>
          <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
            No questions were generated for this practice session. Please try again.
          </p>
          <button onClick={onBack} className="mc-button mc-button-primary">
            Back to Practice Setup
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    const scoreEmoji = sessionScore >= 80 ? '🎉' : sessionScore >= 60 ? '👏' : '💪';

    return (
      <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div className="mc-card" style={{ maxWidth: '600px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>{scoreEmoji}</div>

          <h2 className="mc-heading-1" style={{ color: 'var(--mc-primary)', marginBottom: '1rem' }}>
            Practice Complete!
          </h2>

          <div className="mc-score-display" style={{ marginBottom: '2rem' }}>
            {sessionScore}%
          </div>

          {streak > 3 && (
            <div className="mc-streak-indicator" style={{ marginBottom: '1.5rem' }}>
              🔥 {streak} Question Streak!
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
            <div className="mc-card" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.5rem' }}>
                Questions Answered
              </div>
              <div className="mc-heading-1" style={{ color: 'var(--mc-primary)' }}>
                {Object.keys(answers).length}/{questions.length}
              </div>
            </div>

            <div className="mc-card" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.5rem' }}>
                Correct Answers
              </div>
              <div className="mc-heading-1" style={{ color: 'var(--mc-primary)' }}>
                {questions.filter((question, index) => {
                  const userAnswer = answers[index];
                  let correctAnswerIndex;
                  if (question.correct_index !== undefined) {
                    correctAnswerIndex = question.correct_index;
                  } else if (typeof question.correct_answer === 'string' && question.correct_answer.match(/^[A-D]$/)) {
                    correctAnswerIndex = question.correct_answer.charCodeAt(0) - 'A'.charCodeAt(0);
                  } else {
                    correctAnswerIndex = question.options.indexOf(question.correct_answer);
                  }
                  return userAnswer === correctAnswerIndex;
                }).length}
              </div>
            </div>

            <div className="mc-card" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.5rem' }}>
                Subtopic
              </div>
              <div className="mc-heading-3" style={{ color: 'var(--mc-primary)' }}>
                {sessionInfo.subtopic}
              </div>
            </div>

            <div className="mc-card" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.5rem' }}>
                Time Used
              </div>
              <div className="mc-heading-3" style={{ color: 'var(--mc-primary)' }}>
                {Math.round((totalDuration - timeRemaining) / 60)} min
              </div>
            </div>
          </div>

          <div className="mc-card" style={{ background: 'var(--mc-primary-lighter)', marginBottom: '1.5rem', padding: '1rem' }}>
            <p className="mc-body" style={{ color: 'var(--mc-charcoal)' }}>
              <strong>Great practice session!</strong>
              {sessionScore >= 80 && " Outstanding performance!"}
              {sessionScore >= 60 && sessionScore < 80 && " Good work! Keep practicing!"}
              {sessionScore < 60 && " Don't worry, practice makes perfect! Try focusing on areas that need improvement."}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
              onClick={() => onComplete(sessionScore)}
              className="mc-button mc-button-primary mc-button-large w-full"
            >
              Continue Practice
            </button>

            <button
              onClick={onBack}
              className="mc-button mc-button-outline mc-button-large w-full"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const hasAnswer = answers[currentQuestionIndex] !== undefined;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const progressPercentage = ((currentQuestionIndex + 1) / questions.length) * 100;
  const timerProgressPercentage = timeRemaining !== null && totalDuration > 0
    ? Math.round((timeRemaining / totalDuration) * 100)
    : 0;

  const getTimerClass = () => {
    if (timeRemaining === null || totalDuration === 0) return 'mc-timer';
    const percentageLeft = (timeRemaining / totalDuration) * 100;
    if (percentageLeft > 50) return 'mc-timer';
    if (percentageLeft > 20) return 'mc-timer mc-timer-warning';
    return 'mc-timer mc-timer-danger';
  };

  return (
    <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh' }}>
      <UniversalHeader
        currentPage="practice"
        practiceSessionActive={true}
        selectedTopic={{ name: sessionInfo.subtopic }}
        breadcrumbs={[
          { label: 'Dashboard', action: null, current: false },
          { label: 'Learning Paths', action: null, current: false },
          { label: sessionInfo.subtopic || 'Practice', action: null, current: false },
          { label: 'Practice Session', action: null, current: true }
        ]}
      />

      {/* Session Header */}
      <div className="mc-card" style={{ margin: '1rem auto', maxWidth: '1200px', padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={pauseSession} className="mc-button mc-button-outline mc-button-small" title="Pause session">
              Pause
            </button>
            <button onClick={skipToEnd} className="mc-button mc-button-outline mc-button-small" title="Finish early">
              Finish
            </button>
            <div>
              <h1 className="mc-heading-2" style={{ color: 'var(--mc-primary)', marginBottom: '0.25rem' }}>
                {sessionInfo.subtopic} Practice
              </h1>
              <p className="mc-body-small" style={{ color: 'var(--mc-gray-600)' }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div className={getTimerClass()}>
              <span style={{ fontSize: '1.5rem' }}>⏱️</span>
              <div>
                <div className="mc-timer-value">{formatTime(timeRemaining)}</div>
                <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)', textAlign: 'center' }}>remaining</div>
              </div>
            </div>

            {streak > 1 && (
              <div className="mc-streak-indicator">
                🔥 {streak} Streak!
              </div>
            )}

            <div className="mc-progress-circle" style={{ width: '80px', height: '80px', position: 'relative' }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}>
                <span className="mc-heading-3" style={{ color: 'var(--mc-charcoal)' }}>
                  {Math.round(progressPercentage)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mc-progress-bar" style={{ marginTop: '1.5rem' }}>
          <div className="mc-progress-fill" style={{ width: `${progressPercentage}%` }} />
        </div>
      </div>

      {/* Question Display */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem 2rem' }}>
        <div className="mc-question-card">
          <EnhancedQuestionDisplay
            question_text={currentQuestion.question_text}
            options={currentQuestion.options}
            selectedAnswer={answers[currentQuestionIndex]}
            onAnswerSelect={(answerIndex) => handleAnswerSelect(currentQuestionIndex, answerIndex)}
            explanation={currentQuestion.explanation}
            hint={currentQuestion.hint}
            showExplanation={false}
            has_diagram={false}
            diagram_svg={null}
            isTestMode={true}
          />

          {/* Navigation */}
          <div style={{
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '2px solid var(--mc-gray-200)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <button
              onClick={previousQuestion}
              disabled={currentQuestionIndex === 0}
              className="mc-button mc-button-outline"
              style={currentQuestionIndex === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              ← Previous
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className="mc-body" style={{ color: hasAnswer ? 'var(--mc-success)' : 'var(--mc-gray-600)' }}>
                {hasAnswer ? '✅ Answered' : 'Select an answer'}
              </span>
              <button onClick={nextQuestion} className="mc-body-small" style={{
                background: 'none',
                border: 'none',
                color: 'var(--mc-gray-600)',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}>
                Skip Question
              </button>
            </div>

            <button
              onClick={nextQuestion}
              className="mc-button mc-button-primary"
            >
              {isLastQuestion ? 'Finish Practice' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedPracticeSession;
